import React from 'react';
import { Link } from 'react-router-dom';
import { useAnalytics, useLinkedAgents, useRefreshAnalytics } from '../hooks/useAnalytics';
import { useTheme } from '../context/ThemeContext';
import StatCard from '../components/StatCard';
import { statsIcons } from '../components/ui/StatsIcons';

const Analytics: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { data: linkedAgents, isLoading: linkedAgentsLoading } = useLinkedAgents();
  const { data: analytics, isLoading: analyticsLoading, error } = useAnalytics();
  const refreshAnalytics = useRefreshAnalytics();

  const loading = linkedAgentsLoading || analyticsLoading;

  const formatDuration = (seconds: number | null) => {
    if (seconds === null || seconds === undefined) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null || amount === undefined) return 'N/A';
    return `${amount.toFixed(4)}`;
  };

  const getMetricValue = (key: string) => {
    if (!analytics?.metrics) return null;
    const metric = analytics.metrics.find(m => m.key === key);
    return metric?.value ?? null;
  };

  const calculateSuccessRate = () => {
    const totalCalls = (getMetricValue('calls_dropped_by_customer') as number || 0) +
      (getMetricValue('calls_ended_by_agent') as number || 0);
    const successfulCalls = getMetricValue('calls_ended_by_agent') as number || 0;

    if (totalCalls === 0) return 0;
    return Math.round((successfulCalls / totalCalls) * 100);
  };

  // Loading state with skeletons
  if (loading) {
    return (
      <div className="space-y-4 md:space-y-6">
        <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Analytics</h1>
          <div className={`w-20 h-10 rounded-lg animate-pulse ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {[...Array(4)].map((_, i) => (
            <div key={i} className={`rounded-2xl border shadow-sm p-5 sm:p-6 min-h-[132px] ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}>
              <div className="animate-pulse">
                <div className={`h-4 rounded mb-3 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                <div className={`h-8 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
              </div>
            </div>
          ))}
        </div>

        {/* Call Summary Loading */}
        <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
          <div className="animate-pulse">
            <div className={`h-6 w-32 rounded mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[...Array(2)].map((_, i) => (
                <div key={i}>
                  <div className={`h-4 w-20 rounded mb-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                  <div className={`h-10 w-16 rounded mb-1 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                  <div className={`h-3 w-24 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-4 md:space-y-6">
        <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Analytics</h1>
          <button
            onClick={refreshAnalytics}
            className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${isDarkMode
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
          >
            Retry
          </button>
        </div>

        <div className={`border rounded-lg p-4 ${isDarkMode
          ? 'bg-red-900/20 border-red-800 text-red-300'
          : 'bg-red-50 border-red-200 text-red-800'
          }`}>
          <p>Failed to load analytics. Please try again.</p>
        </div>
      </div>
    );
  }

  // Empty state - no agents linked
  if (analytics?.is_empty || (!linkedAgents || linkedAgents.length === 0)) {
    return (
      <div className="space-y-4 md:space-y-6">
        <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Analytics</h1>
          <button
            onClick={refreshAnalytics}
            className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${isDarkMode
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
          >
            Refresh
          </button>
        </div>

        {/* Empty State Card */}
        <div className={`rounded-2xl border p-8 text-center ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
          <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
            }`}>
            <svg className={`w-8 h-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            No agents linked
          </h3>
          <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Link an assistant or workflow to view analytics.
          </p>
          <Link
            to="/agents"
            className={`inline-flex items-center px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${isDarkMode
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
          >
            Link Agent
          </Link>
        </div>
      </div>
    );
  }

  // Main analytics display
  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Analytics</h1>
        <button
          onClick={refreshAnalytics}
          className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${isDarkMode
            ? 'bg-blue-600 hover:bg-blue-700 text-white'
            : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
        >
          Refresh
        </button>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        <StatCard
          title="Calls Dropped by Customer"
          value={getMetricValue('calls_dropped_by_customer') ?? 0}
          icon={statsIcons.callsDropped}
          colorType="negative"
          trend="down"
          trendValue="2.1%"
        />

        <StatCard
          title="Calls Ended by Agent"
          value={getMetricValue('calls_ended_by_agent') ?? 0}
          icon={statsIcons.callsEnded}
          colorType="positive"
          trend="up"
          trendValue="5.3%"
        />

        <StatCard
          title="Average Duration"
          value={formatDuration(getMetricValue('average_duration_seconds') as number)}
          icon={statsIcons.averageDuration}
          colorType="neutral"
          trend="up"
          trendValue="12s"
        />

        {/* <StatCard
          title="Average Cost"
          value={formatCurrency(getMetricValue('average_cost') as number)}
          icon={statsIcons.averageCost}
          colorType="positive"
          trend="down"
          trendValue="$0.03"
        /> */}
      </div>

      {/* Call Summary Section */}
      <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
        <h2 className={`text-lg font-semibold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Call Summary
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Total Calls */}
          <div>
            <div className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Total Calls
            </div>
            <div className={`text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {((getMetricValue('calls_dropped_by_customer') as number || 0) +
                (getMetricValue('calls_ended_by_agent') as number || 0))}
            </div>
            <div className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Total call volume
            </div>
          </div>

          {/* Success Rate */}
          <div>
            <div className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Success Rate
            </div>
            <div className={`text-4xl font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
              {calculateSuccessRate()}%
            </div>
            <div className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Successful calls
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;